import styled from "styled-components";

// Responsive value can be a single value, array, or object with breakpoints
export type ResponsiveValue<T> = T | Array<T | null> | { [key in number]?: T };

// Helper function to create media queries
const createResponsiveStyles = <T,>(
  property: string,
  value: ResponsiveValue<T>,
  transform: (val: T) => string = (val) => String(val)
): string => {
  // Handle array syntax [base, 768px, 1024px, etc]
  if (Array.isArray(value)) {
    return value
      .map((v, i) => {
        if (v === null) return "";
        if (i === 0) return `${property}: ${transform(v)};`;
        return `
          @media (min-width: ${i}px) {
            ${property}: ${transform(v)};
          }
        `;
      })
      .join("\n");
  }

  // Handle object syntax { 768: value, 1024: value }
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return Object.entries(value as Record<string, T>)
      .map(([breakpoint, v]) => {
        if (breakpoint === "0") return `${property}: ${transform(v!)};`;
        return `
          @media (min-width: ${breakpoint}px) {
            ${property}: ${transform(v!)};
          }
        `;
      })
      .join("\n");
  }

  // Handle single value
  return `${property}: ${transform(value)};`;
};

// Props that should not be forwarded to the DOM
const systemProps = [
  "display",
  "flexDirection",
  "flexWrap",
  "justifyContent",
  "alignItems",
  "flex",
  "gap",
  "width",
  "height",
  "minWidth",
  "maxWidth",
  "minHeight",
  "maxHeight",
  "m",
  "mt",
  "mr",
  "mb",
  "ml",
  "mx",
  "my",
  "p",
  "pt",
  "pr",
  "pb",
  "pl",
  "px",
  "py",
] as const;

// Mimic styled-system prop types
type SpaceValue = string | number;
type DisplayValue = "flex" | "inline-flex" | "none";
type FlexDirectionValue = "row" | "column" | "row-reverse" | "column-reverse";
type FlexWrapValue = "nowrap" | "wrap" | "wrap-reverse";
type JustifyContentValue =
  | "flex-start"
  | "flex-end"
  | "center"
  | "space-between"
  | "space-around"
  | "space-evenly";
type AlignItemsValue =
  | "flex-start"
  | "flex-end"
  | "center"
  | "stretch"
  | "baseline";

interface SpaceProps {
  m?: ResponsiveValue<SpaceValue>;
  mt?: ResponsiveValue<SpaceValue>;
  mr?: ResponsiveValue<SpaceValue>;
  mb?: ResponsiveValue<SpaceValue>;
  ml?: ResponsiveValue<SpaceValue>;
  mx?: ResponsiveValue<SpaceValue>;
  my?: ResponsiveValue<SpaceValue>;
  p?: ResponsiveValue<SpaceValue>;
  pt?: ResponsiveValue<SpaceValue>;
  pr?: ResponsiveValue<SpaceValue>;
  pb?: ResponsiveValue<SpaceValue>;
  pl?: ResponsiveValue<SpaceValue>;
  px?: ResponsiveValue<SpaceValue>;
  py?: ResponsiveValue<SpaceValue>;
}

interface FlexboxProps {
  display?: ResponsiveValue<DisplayValue>;
  flexDirection?: ResponsiveValue<FlexDirectionValue>;
  flexWrap?: ResponsiveValue<FlexWrapValue>;
  justifyContent?: ResponsiveValue<JustifyContentValue>;
  alignItems?: ResponsiveValue<AlignItemsValue>;
  flex?: ResponsiveValue<string>;
  gap?: ResponsiveValue<SpaceValue>;
}

interface LayoutProps {
  width?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
  minWidth?: ResponsiveValue<string>;
  maxWidth?: ResponsiveValue<string>;
  minHeight?: ResponsiveValue<string>;
  maxHeight?: ResponsiveValue<string>;
}

type FlexBoxProps = SpaceProps & FlexboxProps & LayoutProps;

export const FlexBox = styled.div.withConfig({
  componentId: "FlexBox",
  shouldForwardProp: (prop: string) => !systemProps.includes(prop as any),
})<FlexBoxProps>`
  display: flex;
  ${({ display }) => display && createResponsiveStyles("display", display)}
  ${({ flexDirection }) =>
    flexDirection && createResponsiveStyles("flex-direction", flexDirection)}
  ${({ flexWrap }) => flexWrap && createResponsiveStyles("flex-wrap", flexWrap)}
  ${({ justifyContent }) =>
    justifyContent && createResponsiveStyles("justify-content", justifyContent)}
  ${({ alignItems }) =>
    alignItems && createResponsiveStyles("align-items", alignItems)}
  ${({ flex }) => flex && createResponsiveStyles("flex", flex)}
  ${({ gap }) => gap && createResponsiveStyles("gap", gap)}
  
  ${({ width }) => width && createResponsiveStyles("width", width)}
  ${({ height }) => height && createResponsiveStyles("height", height)}
  ${({ minWidth }) => minWidth && createResponsiveStyles("min-width", minWidth)}
  ${({ maxWidth }) => maxWidth && createResponsiveStyles("max-width", maxWidth)}
  ${({ minHeight }) =>
    minHeight && createResponsiveStyles("min-height", minHeight)}
  ${({ maxHeight }) =>
    maxHeight && createResponsiveStyles("max-height", maxHeight)}
  
  ${({ m }) => m && createResponsiveStyles("margin", m)}
  ${({ mt }) => mt && createResponsiveStyles("margin-top", mt)}
  ${({ mr }) => mr && createResponsiveStyles("margin-right", mr)}
  ${({ mb }) => mb && createResponsiveStyles("margin-bottom", mb)}
  ${({ ml }) => ml && createResponsiveStyles("margin-left", ml)}
  ${({ mx }) => mx && createResponsiveStyles("margin-inline", mx)}
  ${({ my }) => my && createResponsiveStyles("margin-block", my)}
  
  ${({ p }) => p && createResponsiveStyles("padding", p)}
  ${({ pt }) => pt && createResponsiveStyles("padding-top", pt)}
  ${({ pr }) => pr && createResponsiveStyles("padding-right", pr)}
  ${({ pb }) => pb && createResponsiveStyles("padding-bottom", pb)}
  ${({ pl }) => pl && createResponsiveStyles("padding-left", pl)}
  ${({ px }) => px && createResponsiveStyles("padding-inline", px)}
  ${({ py }) => py && createResponsiveStyles("padding-block", py)}
`;
